import { useEffect, useState } from "react";

import { useDispatch } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";

import { showErrorToast, showSuccessToast } from "app/actions/toast_actions";
import { useRequestAccessFromAdminMutation } from "app/pages/sales-intelligence/query/mutations/useRequestAccessFromAdminMutation";
import {
    ACCESS_REQUEST_TTL_MS,
    markAccessRequestSent,
    readAccessRequestSentAt,
} from "app/pages/sales-intelligence/store/demo-mode/localStorage";
import type { IDemoSession } from "app/pages/sales-intelligence/types/demo-mode";
import { demoModeKeys } from "app/pages/sales-intelligence/utils/i18n/demo-mode";

/** Both toasts persist until dismissed: `0` leaves the hide timer unarmed, and `ToastList` supplies the close X. */
const NO_AUTO_HIDE = 0;

interface IDemoModeAccessRequest {
    /** True while the demo's request is still within its throttle window, so the CTA should stay hidden. */
    readonly isSent: boolean;
    readonly isPending: boolean;
    readonly requestAccess: VoidFunction;
}

/**
 * Asks the account admin for access to the feature a demo previews, and remembers that we did.
 *
 * Only the request is owned here — the CTA click is a UI event and stays tracked at the click site, so this
 * hook reports nothing about how it was triggered.
 *
 * A successful request is suppressed for `ACCESS_REQUEST_TTL_MS` rather than forever: the admin may simply not
 * act, and the user should be able to nudge again the next day without a second email landing immediately.
 */
export const useDemoModeAccessRequest = (demo: IDemoSession): IDemoModeAccessRequest => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();

    const [sentAt, setSentAt] = useState(readAccessRequestSentAt);
    const { mutate, isPending } = useRequestAccessFromAdminMutation();

    const requestAccess = (): void => {
        mutate(demo.accessRequestFeature, {
            onSuccess: () => {
                setSentAt(markAccessRequestSent(demo.id));
                dispatch(showSuccessToast(translate(demoModeKeys.accessRequestSuccess), undefined, NO_AUTO_HIDE));
            },
            onError: () => {
                dispatch(showErrorToast(translate(demoModeKeys.accessRequestError), undefined, NO_AUTO_HIDE));
            },
        });
    };

    const lastSentAt = sentAt[demo.id];

    // Date.now() can't be read directly during render, so "now" lives in state and only ever
    // advances via a timer scheduled for the moment the TTL actually elapses.
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (lastSentAt === undefined) {
            return;
        }
        const remainingMs = ACCESS_REQUEST_TTL_MS - (Date.now() - lastSentAt);
        if (remainingMs <= 0) {
            return;
        }
        const timeoutId = setTimeout(() => setNow(Date.now()), remainingMs);
        return () => clearTimeout(timeoutId);
    }, [lastSentAt]);

    const isSent = lastSentAt !== undefined && now - lastSentAt < ACCESS_REQUEST_TTL_MS;

    return {
        isSent,
        isPending,
        requestAccess,
    };
};
